import { motion } from 'framer-motion';
import { Linkedin, Mail, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { teamMembers } from '../data';

export function Team() {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.2 });
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section
      id="team"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden bg-[#0a1628]"
    >
      {/* Animated Gradient Blobs Background - Profound */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Blob 1 - Top Left - Large and Bright */}
        <motion.div
          animate={{
            x: [0, 150, 0],
            y: [0, 80, 0],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -top-60 -left-60 w-[700px] h-[700px] bg-gradient-to-br from-blue-500/60 to-primary-600/40 rounded-full blur-[60px]"
        />
        
        {/* Blob 2 - Top Right - Cyan Glow */}
        <motion.div
          animate={{
            x: [0, -180, 0],
            y: [0, 200, 0],
            scale: [1, 1.6, 1],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          className="absolute -top-40 -right-60 w-[600px] h-[600px] bg-gradient-to-br from-cyan-400/50 to-blue-500/35 rounded-full blur-[50px]"
        />
        
        {/* Blob 3 - Bottom Left - Purple/Blue Accent */}
        <motion.div
          animate={{
            x: [0, 150, 0],
            y: [0, -150, 0],
            scale: [1, 1.7, 1],
          }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3,
          }}
          className="absolute -bottom-40 -left-60 w-[650px] h-[650px] bg-gradient-to-tr from-primary-600/55 to-blue-500/40 rounded-full blur-[55px]"
        />
        
        {/* Blob 4 - Bottom Right - Blue Tint */}
        <motion.div
          animate={{
            x: [0, -200, 0],
            y: [0, -120, 0],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 13,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
          className="absolute -bottom-60 right-0 w-[550px] h-[550px] bg-gradient-to-bl from-blue-400/45 to-primary-600/35 rounded-full blur-[50px]"
        />

        {/* Blob 5 - Center - Strong Pulsing */}
        <motion.div
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-blue-400/50 to-cyan-400/40 rounded-full blur-[70px]"
        />

        {/* Blob 6 - Additional Corner Accent */}
        <motion.div
          animate={{
            x: [100, -100, 100],
            y: [-50, 50, -50],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4,
          }}
          className="absolute top-1/4 right-1/4 w-[300px] h-[300px] bg-gradient-to-br from-indigo-500/40 to-blue-400/30 rounded-full blur-[40px]"
        />
      </div>
      <div className="max-w-7xl mx-auto section-padding">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0d1d33] border border-blue-800/50 rounded-full mb-6"
          >
            <span className="w-2 h-2 bg-blue-400 rounded-full" />
            <span className="text-sm font-medium text-blue-300">Tim Kami</span>
          </motion.div>

          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Bertemu dengan
            <span className="text-blue-300"> Tim Ahli</span>
          </h2>

          <p className="text-lg text-blue-200/70">
            Profesional berpengalaman yang siap membantu kesuksesan bisnis Anda
          </p>
        </motion.div>

        {/* Team Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-start"
        >
          {teamMembers.map((member) => (
            <motion.div
              key={member.id}
              variants={itemVariants}
              onMouseEnter={() => setHoveredId(member.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="group relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-blue-900/20 transition-all duration-500 cursor-pointer self-start bg-[#0d1d33] border border-blue-800/50"
            >
              {/* Image Container */}
              <div className="relative h-80 overflow-hidden">
                <motion.img
                  src={member.image}
                  alt={member.name}
                  animate={{ scale: hoveredId === member.id ? 1.05 : 1 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-full object-cover object-top"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: hoveredId === member.id ? 1 : 0, y: hoveredId === member.id ? 0 : 20 }}
                  transition={{ duration: 0.3 }}
                  className="absolute bottom-4 left-4 right-4 flex justify-center gap-3 pointer-events-auto z-20"
                >
                  {[
                    { icon: Linkedin, href: member.linkedin || '#' },
                    { icon: Mail, href: `mailto:info@emporiumsolution.id` },
                  ].map((social, i) => {
                    const Icon = social.icon;
                    return (
                      <motion.a
                        key={i}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-blue-500 transition-colors duration-300 cursor-pointer pointer-events-auto"
                      >
                        <Icon className="w-5 h-5" />
                      </motion.a>
                    );
                  })}
                </motion.div>

                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1d33] via-transparent to-transparent opacity-80" />
              </div>

              {/* Content */}
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-white mb-1 group-hover:text-blue-300 transition-colors duration-300">
                  {member.name}
                </h3>
                <p className="text-blue-300 font-medium mb-2">{member.role}</p>
                
                <p className="text-blue-200/50 text-sm italic mb-2">
                  cari tahu lebih banyak
                </p>
                
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ 
                    height: hoveredId === member.id ? 'auto' : 0,
                    opacity: hoveredId === member.id ? 1 : 0
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="pt-4 border-t border-blue-800/50 mt-4">
                    <p className="text-blue-100/80 text-sm leading-relaxed text-left">
                      {member.bio}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ rotate: hoveredId === member.id ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3"
                >
                  <ChevronDown className="w-5 h-5 text-blue-400/60 mx-auto" />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Join Team CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-blue-200/70">
            Tertarik bergabung dengan tim kami?{' '}
            <a href="#contact" className="text-blue-300 font-semibold hover:underline">
              Hubungi kami
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
